export type NetworkErrorType = 'network' | 'timeout' | 'chunk' | 'server' | 'abort' | 'unknown';

export function isConnectionAbortError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const code = (error as NodeJS.ErrnoException).code;
  const message = error.message || '';
  return (
    code === 'ECONNRESET' ||
    code === 'EPIPE' ||
    message === 'aborted' ||
    message.startsWith('aborted') ||
    error.name === 'AbortError'
  );
}

export function getNetworkErrorType(error: unknown): NetworkErrorType {
  if (!(error instanceof Error)) return 'unknown';

  if (isConnectionAbortError(error)) {
    return 'abort';
  }

  const { message, name } = error;
  const lowerMessage = message.toLowerCase();

  if (name === 'FirstChunkTimeoutError') {
    return 'timeout';
  }

  if (
    error instanceof TypeError &&
    (lowerMessage.includes('load failed') || lowerMessage.includes('failed to fetch'))
  ) {
    return 'network';
  }

  if (error instanceof DOMException && name === 'AbortError') {
    return 'timeout';
  }

  if (lowerMessage.includes('chunk') || name === 'ChunkLoadError') {
    return 'chunk';
  }

  if (lowerMessage.includes('network') || lowerMessage.includes('net::')) {
    return 'network';
  }

  if (/\b5\d{2}\b/.test(message)) {
    return 'server';
  }

  return 'unknown';
}

const RETRYABLE_TYPES: NetworkErrorType[] = ['network', 'timeout', 'chunk', 'server'];

export function isRetryableError(error: unknown): boolean {
  return RETRYABLE_TYPES.includes(getNetworkErrorType(error));
}

export function isNetworkError(error: unknown): boolean {
  return getNetworkErrorType(error) === 'network';
}

/**
 * `fetch` rejects with a TypeError when the request never reached the server, while a transport
 * that did read an HTTP response builds a plain Error from the body — so the type, not the
 * wording, separates a dead connection from a 5xx whose text happens to say "network".
 */
export function isBrowserConnectivityError(error: unknown): boolean {
  return error instanceof TypeError && isNetworkError(error);
}

export function isChunkLoadError(error: unknown): boolean {
  return getNetworkErrorType(error) === 'chunk';
}
